import React, { useContext, useEffect, useState } from "react";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { useTranslation } from "react-i18next";
import { useErrorNotification } from "@common/components/Notification";

import RightDrawer from "@common/components/RightDrawer";
import { getMeetingElog, putMeetingElog } from "@services/meetings";
import Button from "@common/components/Button";

import { ElogMeetingContext, ElogMeetingContextProvider } from "./context";
import { MeetingElog } from "@services/meetings/types";
import { WithGrow } from "@common/components/WithGrow";
import Tabs from "@common/components/Tabs";
import Tab from "@common/components/Tab";
import Typography from "@common/components/Typography";
import TabPanel from "@common/components/TabPanel";
import MeetingOptions from "./MeetingOptions";
import ProducerFeedback from "./ProducerFeedback";
import ReviewTickets from "./ReviewTickets";
import Attendance from "./Attendance";
import Elogs from "./Elogs";
import Billing from "./Billing";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { canViewElogsAndRevisions } from "@common/utils/user";

type WrapUpProps = {
  open: boolean;
  setOpen: Function;
};

const Layout = (props: WrapUpProps) => {
  const { open, setOpen } = props;
  const { t } = useTranslation();
  const context = useContext(RoomsRealtimeContext);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const dispatchError = useErrorNotification();
  const { form, enableAttendanceTracking, showBillingTab, canViewElog } =
    useContext(ElogMeetingContext);
  const onClickReport = async () => {
    setLoadingReport(true);
    try {
      if (form.elogMeeting)
        await putMeetingElog(context.detail?.meeting?.id, {
          ...form.elogMeeting,
          elogDetail: form.details,
          wrappedUp: true,
        });
      context.detail?.refetch();
      setOpen(false);
    } catch (e) {
      dispatchError(e);
    }
    setLoadingReport(false);
  };
  const onClickSave = async () => {
    setLoading(true);
    try {
      if (form.elogMeeting)
        await putMeetingElog(context.detail?.meeting?.id, {
          ...form.elogMeeting,
          elogDetail: form.details,
        });
      context.detail?.refetch();
    } catch (e) {
      dispatchError(e);
    }
    setLoading(false);
  };
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const tabs: {
    label: string;
    badge?: boolean;
    content: React.JSX.Element;
    noPadding?: boolean;
  }[] = [
    { label: "Meeting Options", content: <MeetingOptions /> },
    { label: "Producer Feedback", content: <ProducerFeedback /> },
    { label: "Review Tickets", content: <ReviewTickets /> },
  ];
  if (enableAttendanceTracking) {
    tabs.push({ label: "Attendance", content: <Attendance /> });
  }
  if (canViewElog) {
    tabs.push({ label: "E-Logs", content: <Elogs /> });
  }
  if (showBillingTab && canViewElog) {
    tabs.push({ label: "Billing", content: <Billing /> });
  }
  const renderTabsView = () => (
    <WithGrow
      direction="column"
      fixed={
        <Tabs value={tabValue} onChange={handleTabChange}>
          {tabs.map(({ label, badge }) =>
            badge ? (
              <Tab
                key={label}
                label={<Typography sx={{ mr: 1 }}>{label}</Typography>}
              />
            ) : (
              <Tab key={label} label={<Typography>{t(label)}</Typography>} />
            )
          )}
        </Tabs>
      }
    >
      {tabs.map(({ content, label, noPadding = false }, index) => (
        <TabPanel
          key={label}
          value={tabValue}
          index={index}
          noPadding={noPadding}
        >
          {content}
        </TabPanel>
      ))}
    </WithGrow>
  );

  return (
    <RightDrawer
      title={t("Wrap up Meeting")}
      open={open}
      onClose={() => setOpen(false)}
      width="50%"
      closeButton={[
        <Button
          variant="outlined"
          color="greyed"
          onClick={() => onClickSave()}
          loading={loading}
          disabled={form.elogMeeting == undefined}
          key="saveButton"
        >
          Save
        </Button>,
        <Button
          variant="contained"
          color="white"
          onClick={() => onClickReport()}
          loading={loadingReport}
          disabled={!form.elogMeeting == undefined}
          key="reportButton"
        >
          Report
        </Button>,
        <Button
          color="greyed"
          variant="outlined"
          onClick={() => setOpen(false)}
          key="cancelButton"
        >
          Cancel
        </Button>,
      ]}
    >
      {renderTabsView()}
    </RightDrawer>
  );
};

const WrapUp = (props: WrapUpProps) => {
  const [elogMeeting, setElogMeeting] = useState<MeetingElog>();
  const dispatchError = useErrorNotification();
  const context = useContext(RoomsRealtimeContext);
  const [user] = useAuthenticatedUser();
  const canViewElog = canViewElogsAndRevisions(user);
  const refetch = async (id: number) => {
    try {
      setElogMeeting(await getMeetingElog(id));
    } catch (e) {
      dispatchError(e);
    }
  };

  useEffect(() => {
    if (context.detail?.meeting?.id !== undefined && canViewElog)
      refetch(context.detail?.meeting?.id);
  }, [context.detail?.meeting]);

  return (
    <ElogMeetingContextProvider
      elogMeeting={elogMeeting}
      meeting={context.detail?.meeting}
    >
      <Layout {...props} />
    </ElogMeetingContextProvider>
  );
};
export default WrapUp;
